import { CHART_TYPES, ENTITY_DIMENSIONS } from './chartTypes'
import type { Metric, Dimension, FactTable } from 'AnalyticsDashboard/utils/factTableHelpers'
import { metricDisplayLabel } from 'AnalyticsDashboard/utils/factTableHelpers'

declare const _: any

interface QueryInfo {
  availableDimensions: Dimension[]
}

interface ColumnDef {
  id: string
  source: string | ((row: any, dimInfo: any) => any)
  label: string
  kind?: string
}

interface BuildConfigResult {
  dimensions: Array<{ id: string; timeBucket?: string }>
  metrics: Metric[]
  chartOptions: Record<string, any>
}

export function buildColumns(
  dimensions: Array<{ id: string; timeBucket?: string }>,
  metrics: Metric[],
  availableDimensions: Dimension[],
  factTables: FactTable[] = []
): ColumnDef[] {
  const columns: ColumnDef[] = []
  dimensions.forEach(dim => {
    const dimInfo = availableDimensions.find(d => d.id === dim.id)
    const camelId = _.camelCase(dim.id)
    const entity = ENTITY_DIMENSIONS[dim.id]
    if (entity) {
      columns.push({ id: camelId, source: entity.source, label: entity.label })
    } else if (dimInfo?.kind === 'time') {
      columns.push({ id: camelId, source: camelId, kind: 'date', label: dimInfo.label || 'Time' })
    } else if (dimInfo?.options) {
      columns.push({ id: camelId, source: `${camelId}.name`, label: dimInfo?.label || dim.id })
    } else {
      columns.push({ id: camelId, source: camelId, label: dimInfo?.name || dimInfo?.label || dim.id })
    }
  })

  metrics.forEach(m => {
    columns.push({ id: m.id, source: m.id, label: metricDisplayLabel(m, factTables), kind: 'number' })
  })

  return columns
}

export function buildConfig(
  chartType: string,
  slotValues: Record<string, any>,
  queryInfo: QueryInfo,
  factTables: FactTable[] = []
): BuildConfigResult {
  const chartDef = CHART_TYPES[chartType]
  if (!chartDef) return { dimensions: [], metrics: [], chartOptions: {} }

  // Collect dimensions via chart type definition
  const dimensions = chartDef.collectDimensions(slotValues)

  // Metrics are already full v2 objects from the MetricPicker
  const metrics: Metric[] = Array.isArray(slotValues.metrics) ? slotValues.metrics : slotValues.metrics ? [slotValues.metrics] : []

  const columns = buildColumns(dimensions, metrics, queryInfo.availableDimensions, factTables)

  // Build chart options: common columns + chart-type-specific options
  const chartOptions: Record<string, any> = {
    columns,
    ...chartDef.buildChartOptions(slotValues, metrics, queryInfo.availableDimensions),
  }

  return { dimensions, metrics, chartOptions }
}

export function restoreSlots(config: Record<string, any> | null): Record<string, any> {
  if (!config) return {}

  const chartDef = CHART_TYPES[config.chartType]
  if (!chartDef) return {}

  const slots: Record<string, any> = {}
  const dims: Array<{ id: string; timeBucket?: string }> = config.dimensions || []
  const metrics: Metric[] = config.metrics || []
  const assignedDimIds = new Set<string>()

  // Restore each slot based on its kind
  for (const [key, slot] of Object.entries(chartDef.slots)) {
    if (slot.kind === 'time') {
      const dim = dims.find(d => d.timeBucket) || null
      slots[key] = dim
      if (dim) assignedDimIds.add(dim.id)
    } else if (slot.kind === 'metric') {
      // Metrics are full v2 objects — pass through directly
      slots[key] = slot.single ? (metrics.length > 0 ? [metrics[0]] : []) : metrics
    } else if (slot.kind === 'boolean') {
      slots[key] = config.chartOptions?.[key] ?? false
    } else if (slot.kind === 'html') {
      slots[key] = config.chartOptions?.[key] ?? ''
    } else if (slot.kind === 'geoDimension') {
      const dim = dims.find(d => !assignedDimIds.has(d.id))
      slots[key] = dim || null
      if (dim) assignedDimIds.add(dim.id)
    } else if (slot.kind === 'dimension') {
      const available = dims.filter(d => !assignedDimIds.has(d.id))
      if (slot.single) {
        const dim = available[0] || null
        slots[key] = dim
        if (dim) assignedDimIds.add(dim.id)
      } else {
        slots[key] = available
        available.forEach(d => assignedDimIds.add(d.id))
      }
    }
  }

  return slots
}
